// src/app/modules/user/components/profile-detail/ProfileDetailOverview.tsx
import React, {useState, useEffect} from 'react'
import {useLocation, useParams} from 'react-router-dom'
import {useReduxAuth} from '../../../auth'
import moment from 'moment'
import axios from 'axios'
import {FiUser, FiMail, FiPhone, FiMapPin, FiCalendar, FiEdit3, FiHome, FiTag, FiRefreshCw} from 'react-icons/fi'
import PhoneLink from '../common/PhoneLink'
import {RoleGuard} from '../permissions/RoleGuard'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import MetaTags from '../../../../meta/MetaTags'
import PrimaryButton from '../common/buttons/PrimaryButton'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'

interface ProfileDetailOverviewProps {
  user?: any
  onEditPress?: () => void
  isLoading: boolean
}

export function ProfileDetailOverview({user, onEditPress, isLoading}: ProfileDetailOverviewProps) {
  const {currentUser} = useReduxAuth()
  const location = useLocation()
  const {patientId} = useParams<{patientId: string}>()
  const roleId = currentUser?.role_id
  
  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  
  const [billingCycle, setBillingCycle] = useState<any>(null)
  const [billingCycleLoading, setBillingCycleLoading] = useState(false)

  // Fetch billing cycle data when the component mounts and we have a patient
  useEffect(() => {
    const fetchBillingCycle = async () => {
      if (!patientId || !location.pathname.includes('patient-details')) return

      setBillingCycleLoading(true)
      try {
        // Build query params - include doctor_id for MA
        const doctorIdParam = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
          ? `doctor_id=${maSelectedDoctorId}`
          : null
        const response = await axios.get(API_ENDPOINTS.GET_PATIENT_LATEST_BILLING_CYCLE(patientId, doctorIdParam))
        setBillingCycle(response.data?.data?.current_cycle)
      } catch (error) {
        console.log('Could not fetch billing cycle for patient details:', error)
        setBillingCycle(null)
      } finally {
        setBillingCycleLoading(false)
      }
    }

    fetchBillingCycle()
  }, [patientId, location.pathname])

  const DetailItem = ({
    icon: Icon,
    label,
    value,
  }: {
    icon: React.ElementType
    label: string
    value?: string | null | JSX.Element
  }) => (
    <div className='d-flex align-items-start gap-5 py-4 border-bottom'>
      <div className='d-flex align-items-center gap-3 details-width'>
        <Icon className='text-primary' size={20} />
        <span className='text-gray-600 fs-6 fw-semibold '>{label}</span>
      </div>
      <div className='flex-grow-1'>
        <span className='text-dark fs-6 fw-medium'>
          {value ?? <span className='text-muted'>Not provided</span>}
        </span>
      </div>
    </div>
  )

  const truncateText = (text: string, length = 60) => {
    if (!text) return ''
    return text.length > length ? text.slice(0, length) + '...' : text
  }

  // derive a safe consent file name from available data (explicit name, URL, or default)
  const consentFileName = (() => {
    try {
      const nameFromUrl = user?.consent_form_url
        ? decodeURIComponent(user.consent_form_url.split('/').pop().split('?')[0])
        : null
      return user?.consent_form_name || nameFromUrl || 'consent_form.pdf'
    } catch (e) {
      return user?.consent_form_name || 'consent_form.pdf'
    }
  })()

  if (isLoading) return null

  return (
    <>
      <MetaTags title={user?.full_name + ': Profile Details'} />

      <div className='card rounded-3 shadow-lg border-0 mb-5'>
        <div className='card-body p-0'>
          {/* Header */}
          <div className='d-flex justify-content-between align-items-center p-6 bg-light-primary rounded-top-3'>
            <h3 className='m-0 fs-2 fw-bold text-gray-800 d-flex align-items-center gap-3'>
              <FiUser size={28} className='text-primary' />
              Profile Details
            </h3>

            {/* Edit button visible only to doctors, nurses, and medical assistants */}
            {location.pathname.includes('patient-details') && (
              <RoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
                <PrimaryButton onClick={onEditPress} className='gap-2 px-5 py-3 rounded-2'>
                  <FiEdit3 size={18} />
                  Edit Profile
                </PrimaryButton>
              </RoleGuard>
            )}
            {location.pathname.includes('doctor-details') && (
              <RoleGuard allowedRoles={[USER_ROLES.ADMIN]}>
                <PrimaryButton onClick={onEditPress} className='gap-2 px-5 py-3 rounded-2'>
                  <FiEdit3 size={18} />
                  Edit Profile
                </PrimaryButton>
              </RoleGuard>
            )}
            {/* Edit button for staff (nurse-details) - visible to doctors and medical assistants */}
            {location.pathname.includes('nurse-details') && onEditPress && (
              <RoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.MEDICAL_ASSISTANT]}>
                <PrimaryButton onClick={onEditPress} className='gap-2 px-5 py-3 rounded-2'>
                  <FiEdit3 size={18} />
                  Edit Profile
                </PrimaryButton>
              </RoleGuard>
            )}
          </div>

          {/* Body */}
          <div className='p-6'>
            <DetailItem icon={FiUser} label='Full Name' value={user?.full_name ?? null} />
            <DetailItem icon={FiMail} label='Email Address' value={truncateText(user?.email) ?? null} />
            {user?.phone_number && (
              <DetailItem
                icon={FiPhone}
                label='Phone Number'
                value={<PhoneLink phone={user.phone_number} />}
              />
            )}
            {user?.address && <DetailItem icon={FiMapPin} label='Address' value={truncateText(user.address)} />}
            {user?.dob && (
              <DetailItem
                icon={FiCalendar}
                label='Date of Birth'
                value={moment.utc(user.dob).format('MM/DD/YYYY')}
              />
            )}

            {/* Consent Form - only show for patients, not for staff */}
            {location.pathname.includes('patient-details') && (
              <>
                {user?.consent_form_url ? (
                  <div className='d-flex align-items-center gap-4 flex-wrap py-4 border-bottom'>
                    <div className='d-flex align-items-center gap-3 details-width'>
                      <FiUser className='text-primary' size={20} />
                      <span className='text-gray-600 fs-6 fw-semibold'>Consent Form</span>
                    </div>

                    <div
                      className='d-flex align-items-center justify-content-between p-2 rounded shadow-sm'
                      style={{
                        backgroundColor: '#f9f9f9',
                        border: '1px solid #e0e0e0',
                        minWidth: '200px',
                      }}
                    >
                      <span className='fw-bold text-truncate p-1 px-4' style={{ maxWidth: '160px', color: '#5e5873' }}>
                        Download
                      </span>

                      <a
                        href={user.consent_form_url}
                        download
                        target='_blank'
                        rel='noopener noreferrer'
                        className='btn d-flex justify-content-center align-items-center'
                        style={{
                          backgroundColor: '#6f42c1',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          width: "40px",
                          height: "40px",
                        }}
                        title='Download Consent Form'
                      >
                        <i className='bi bi-download p-1' style={{ color: '#fff', fontSize: '18px' }}></i>
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className='d-flex align-items-start gap-5 py-4 border-bottom'>
                    <div className='d-flex align-items-center gap-3 details-width'>
                      <FiUser className='text-primary' size={20} />
                      <span className='text-gray-600 fs-6 fw-semibold'>Consent Form</span>
                    </div>
                    <div className='flex-grow-1'>
                      <span className='text-muted'>Not provided</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {user?.stripe_customer_id && (
              <DetailItem
                icon={FiUser}
                label='Stripe Customer ID'
                value={user.stripe_customer_id}
              />
            )}
            {user?.stripe_subscription_id && (
              <DetailItem
                icon={FiUser}
                label='Stripe Subscription ID'
                value={user.stripe_subscription_id}
              />
            )}

            {/* Clinic Settings - only show for doctor-details and admin */}
            {location.pathname.includes('doctor-details') && roleId === USER_ROLES.ADMIN && (
              <>
                <DetailItem
                  icon={FiHome}
                  label='Clinic Name'
                  value={user?.clinic_name}
                />
                <DetailItem
                  icon={FiTag}
                  label='Clinic Alias'
                  value={user?.clinic_alias}
                />
                <DetailItem
                  icon={FiRefreshCw}
                  label='Auto-Convert Loaned to Rental'
                  value={
                    <span className={user?.auto_convert_loaned_to_rental !== false ? 'text-success' : 'text-danger'}>
                      {user?.auto_convert_loaned_to_rental !== false ? 'Enabled' : 'Disabled'}
                    </span>
                  }
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
